import { useEffect, useState } from 'react';

import { getCommonTeams } from '../services/individualBioService';
import { SortConfig, Team } from '../types/thematicGroups';

interface UseThematicGroupsProps {
  userUuid1: string;
  userUuid2: string;
}

interface UseThematicGroupsReturn {
  commonTeams: Team[];
  otherTeams: Team[];
  allTeams: Team[];
  loading: boolean;
  error: string | null;
  sortConfig: SortConfig;
  setSortConfig: (config: SortConfig) => void;
  sortedTeams: Team[];
}

const useThematicGroups = ({
  userUuid1,
  userUuid2,
}: UseThematicGroupsProps): UseThematicGroupsReturn => {
  const [commonTeams, setCommonTeams] = useState<Team[]>([]);
  const [otherTeams, setOtherTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'in_common',
    direction: 'desc',
  });

  useEffect(() => {
    const fetchCommonTeams = async () => {
      if (!userUuid1 || !userUuid2) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const data = await getCommonTeams(userUuid1, userUuid2);
        setCommonTeams(data.common_teams || []);
        setOtherTeams(data.other_teams || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro desconhecido');
      } finally {
        setLoading(false);
      }
    };

    fetchCommonTeams();
  }, [userUuid1, userUuid2]);

  const allTeams = [...commonTeams, ...otherTeams];

  const sortTeams = (teams: Team[], config: SortConfig): Team[] => {
    return [...teams].sort((a, b) => {
      // Primeiro, separa grupos em comum dos outros grupos
      const aIsCommon = commonTeams.some(commonTeam => commonTeam.uuid === a.uuid);
      const bIsCommon = commonTeams.some(commonTeam => commonTeam.uuid === b.uuid);

      // Se um é comum e o outro não, o comum vem primeiro
      if (aIsCommon && !bIsCommon) return -1;
      if (!aIsCommon && bIsCommon) return 1;

      // Se ambos são do mesmo tipo (comum ou não comum), ordena pelo critério selecionado
      let aValue: string | number;
      let bValue: string | number;

      switch (config.field) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'total_members':
          aValue = a.total_members;
          bValue = b.total_members;
          break;
        case 'in_common':
          aValue = a.in_common_connections.length;
          bValue = b.in_common_connections.length;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        if (config.direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else {
        if (config.direction === 'asc') {
          return (aValue as number) - (bValue as number);
        } else {
          return (bValue as number) - (aValue as number);
        }
      }
    });
  };

  const sortedTeams = sortTeams(allTeams, sortConfig);

  return {
    commonTeams,
    otherTeams,
    allTeams,
    loading,
    error,
    sortConfig,
    setSortConfig,
    sortedTeams,
  };
};

export default useThematicGroups;
